import type { AgentChatSessionSummaryContract } from '@nest-react/domain';
import type { StateCreator } from 'zustand';

import {
  AGENT_CHAT_HISTORY_LIMIT,
  AGENT_HOME_RECENT_SESSION_LIMIT,
  listAgentChatSessions,
} from '~/repositories/agentChatSessionRepository';
import {
  type SessionPreviewMergeMode,
  cloneSessionSummaryList,
  removeSessionFromList,
  upsertSessionByRecency,
} from '~/repositories/agentChatSessionListEvents';

import type { WorkspaceStore } from '../workspaceStore';
import {
  type WorkspaceAgentSessionListSnapshot,
  emptyAgentSessionListSnapshot,
} from '../types';

type WorkspaceAgentSessionsBucket = {
  recent: WorkspaceAgentSessionListSnapshot;
  all: WorkspaceAgentSessionListSnapshot;
};

export type AgentSessionsSlice = {
  agentSessionsByWorkspace: Record<string, WorkspaceAgentSessionsBucket>;
  fetchRecentAgentSessions: (
    workspaceId: string,
    limit?: number
  ) => Promise<void>;
  fetchAllAgentSessions: (workspaceId: string, limit?: number) => Promise<void>;
  loadMoreAllAgentSessions: (workspaceId: string) => Promise<void>;
  upsertWorkspaceAgentSession: (
    workspaceId: string,
    session: AgentChatSessionSummaryContract,
    options?: { previewMode?: SessionPreviewMergeMode }
  ) => void;
  removeWorkspaceAgentSession: (workspaceId: string, sessionId: string) => void;
  resetWorkspaceAgentSessions: (workspaceId?: string) => void;
};

const emptyBucket = (): WorkspaceAgentSessionsBucket => ({
  recent: emptyAgentSessionListSnapshot(),
  all: emptyAgentSessionListSnapshot(),
});

const patchBucket = (
  current: Record<string, WorkspaceAgentSessionsBucket>,
  workspaceId: string,
  patch: Partial<WorkspaceAgentSessionsBucket>
): Record<string, WorkspaceAgentSessionsBucket> => ({
  ...current,
  [workspaceId]: {
    ...(current[workspaceId] ?? emptyBucket()),
    ...patch,
  },
});

const patchList = (
  current: Record<string, WorkspaceAgentSessionsBucket>,
  workspaceId: string,
  key: keyof WorkspaceAgentSessionsBucket,
  patch: Partial<WorkspaceAgentSessionListSnapshot>
): Record<string, WorkspaceAgentSessionsBucket> =>
  patchBucket(current, workspaceId, {
    [key]: {
      ...((current[workspaceId] ?? emptyBucket())[key] ??
        emptyAgentSessionListSnapshot()),
      ...patch,
    },
  });

export const createAgentSessionsSlice: StateCreator<
  WorkspaceStore,
  [],
  [],
  AgentSessionsSlice
> = (set, get) => ({
  agentSessionsByWorkspace: {},
  fetchRecentAgentSessions: async (
    workspaceId,
    limit = AGENT_HOME_RECENT_SESSION_LIMIT
  ) => {
    set(state => ({
      agentSessionsByWorkspace: patchList(
        state.agentSessionsByWorkspace,
        workspaceId,
        'recent',
        { loading: true }
      ),
    }));
    try {
      const page = await listAgentChatSessions(workspaceId, limit);
      set(state => ({
        agentSessionsByWorkspace: patchList(
          state.agentSessionsByWorkspace,
          workspaceId,
          'recent',
          {
            items: cloneSessionSummaryList(page.items),
            loading: false,
            loaded: true,
            nextCursor: null,
          }
        ),
      }));
    } catch {
      set(state => ({
        agentSessionsByWorkspace: patchList(
          state.agentSessionsByWorkspace,
          workspaceId,
          'recent',
          {
            items: [],
            loading: false,
            loaded: true,
          }
        ),
      }));
    }
  },
  fetchAllAgentSessions: async (
    workspaceId,
    limit = AGENT_CHAT_HISTORY_LIMIT
  ) => {
    set(state => ({
      agentSessionsByWorkspace: patchList(
        state.agentSessionsByWorkspace,
        workspaceId,
        'all',
        { loading: true }
      ),
    }));
    try {
      const page = await listAgentChatSessions(workspaceId, limit);
      set(state => ({
        agentSessionsByWorkspace: patchList(
          state.agentSessionsByWorkspace,
          workspaceId,
          'all',
          {
            items: cloneSessionSummaryList(page.items),
            loading: false,
            loadingMore: false,
            loadMoreError: false,
            loaded: true,
            nextCursor: page.nextCursor,
          }
        ),
      }));
    } catch {
      set(state => ({
        agentSessionsByWorkspace: patchList(
          state.agentSessionsByWorkspace,
          workspaceId,
          'all',
          {
            items: [],
            loading: false,
            loadingMore: false,
            loadMoreError: false,
            loaded: true,
            nextCursor: null,
          }
        ),
      }));
    }
  },
  loadMoreAllAgentSessions: async workspaceId => {
    const snapshot =
      get().agentSessionsByWorkspace[workspaceId]?.all ??
      emptyAgentSessionListSnapshot();
    const cursor = snapshot.nextCursor;
    if (!cursor || snapshot.loading || snapshot.loadingMore) return;

    set(state => ({
      agentSessionsByWorkspace: patchList(
        state.agentSessionsByWorkspace,
        workspaceId,
        'all',
        { loadingMore: true, loadMoreError: false }
      ),
    }));
    try {
      const page = await listAgentChatSessions(
        workspaceId,
        AGENT_CHAT_HISTORY_LIMIT,
        cursor
      );
      set(state => {
        const current =
          state.agentSessionsByWorkspace[workspaceId]?.all ??
          emptyAgentSessionListSnapshot();
        if (current.nextCursor !== cursor) return state;
        const knownIds = new Set(current.items.map(item => item.id));
        return {
          agentSessionsByWorkspace: patchList(
            state.agentSessionsByWorkspace,
            workspaceId,
            'all',
            {
              items: [
                ...current.items,
                ...cloneSessionSummaryList(page.items).filter(
                  item => !knownIds.has(item.id)
                ),
              ],
              loadingMore: false,
              loadMoreError: false,
              nextCursor: page.nextCursor,
            }
          ),
        };
      });
    } catch {
      set(state => {
        const current = state.agentSessionsByWorkspace[workspaceId]?.all;
        if (!current || current.nextCursor !== cursor) return state;
        return {
          agentSessionsByWorkspace: patchList(
            state.agentSessionsByWorkspace,
            workspaceId,
            'all',
            { loadingMore: false, loadMoreError: true }
          ),
        };
      });
    }
  },
  upsertWorkspaceAgentSession: (workspaceId, session, options) => {
    const previewMode = options?.previewMode ?? 'replace';
    set(state => {
      const bucket =
        state.agentSessionsByWorkspace[workspaceId] ?? emptyBucket();
      const nextRecent = upsertSessionByRecency(
        bucket.recent.items,
        session,
        AGENT_HOME_RECENT_SESSION_LIMIT,
        previewMode
      );
      const nextAll = upsertSessionByRecency(
        bucket.all.items,
        session,
        undefined,
        previewMode
      );
      if (nextRecent === bucket.recent.items && nextAll === bucket.all.items) {
        return state;
      }
      return {
        agentSessionsByWorkspace: patchBucket(
          state.agentSessionsByWorkspace,
          workspaceId,
          {
            recent: {
              ...bucket.recent,
              items: nextRecent,
            },
            all: {
              ...bucket.all,
              items: nextAll,
            },
          }
        ),
      };
    });
  },
  removeWorkspaceAgentSession: (workspaceId, sessionId) => {
    set(state => {
      const bucket =
        state.agentSessionsByWorkspace[workspaceId] ?? emptyBucket();
      return {
        agentSessionsByWorkspace: patchBucket(
          state.agentSessionsByWorkspace,
          workspaceId,
          {
            recent: {
              ...bucket.recent,
              items: removeSessionFromList(bucket.recent.items, sessionId),
            },
            all: {
              ...bucket.all,
              items: removeSessionFromList(bucket.all.items, sessionId),
            },
          }
        ),
      };
    });
  },
  resetWorkspaceAgentSessions: workspaceId => {
    if (!workspaceId) {
      set({ agentSessionsByWorkspace: {} });
      return;
    }
    set(state => {
      const next = { ...state.agentSessionsByWorkspace };
      delete next[workspaceId];
      return { agentSessionsByWorkspace: next };
    });
  },
});
